/**
 * Scent Memory — Brand-Unique Interaction System
 *
 * "Scent is Memory." When users interact with fragrance note words,
 * ghostly memory-words appear nearby — connecting scent to personal
 * experience. A poetic micro-interaction only a perfume brand could do.
 */
(function () {
  'use strict';

  const SCENT_MEMORIES = {
    'vanilla': ['grandmother\'s kitchen', 'warm blankets', 'Sunday morning', 'childhood'],
    'amber': ['golden hour', 'autumn walk', 'candlelight', 'slow evening'],
    'rose': ['first love', 'handwritten letters', 'garden gate', 'silk dress'],
    'leather': ['old bookshop', 'father\'s study', 'rainy drive', 'quiet strength'],
    'musk': ['close embrace', 'midnight', 'whispered words', 'fading perfume'],
    'bergamot': ['morning light', 'fresh start', 'open window', 'citrus breeze'],
    'cardamom': ['spice market', 'warm chai', 'monsoon rain', 'faraway place'],
    'tobacco': ['jazz club', 'mahogany desk', 'aged pages', 'gentleman'],
    'patchouli': ['velvet curtains', 'vintage shop', 'incense trail', 'boho summer'],
    'sandalwood': ['temple steps', 'meditation', 'sacred space', 'stillness'],
    'jasmine': ['night garden', 'balcony breeze', 'southern evening', 'white silk'],
    'lavender': ['country road', 'linen closet', 'purple field', 'deep breath'],
    'oakmoss': ['forest floor', 'morning dew', 'mossy stone', 'green shade'],
    'toffee': ['county fair', 'pocket warmth', 'sweet tooth', 'golden wrapper'],
    'pineapple': ['tropical sun', 'sea breeze', 'poolside', 'fresh cut'],
    'cedar': ['mountain cabin', 'pine forest', 'wood smoke', 'winter lodge'],
    'vetiver': ['earth after rain', 'deep roots', 'green shadow', 'quiet power'],
    'violet': ['spring dawn', 'hidden path', 'pressed flower', 'gentle heart'],
    'default': ['a distant memory', 'something familiar', 'almost forgotten', 'once upon a time']
  };

  /* General pool for brand-story scent trail */
  const STORY_MEMORIES = [
    'a distant memory', 'warm skin', 'fading light', 'soft linen',
    'old photographs', 'evening air', 'first rain', 'quiet room',
    'pressed flowers', 'slow morning', 'familiar warmth', 'silk and shadow',
    'candlelit hours', 'garden dusk', 'amber glow', 'gentle haze'
  ];

  const prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
  const isTouchDevice = 'ontouchstart' in window || navigator.maxTouchPoints > 0;
  let activeMemory = null;

  /**
   * Pick a random item from an array.
   */
  function pick(arr) {
    return arr[Math.floor(Math.random() * arr.length)];
  }

  /**
   * Generate a random offset between min and max, with random sign.
   */
  function randomOffset(min, max) {
    const magnitude = min + Math.random() * (max - min);
    return (Math.random() < 0.5 ? -1 : 1) * magnitude;
  }

  /**
   * Create and show a memory word near a target element.
   * Returns the memory span element for cleanup.
   */
  function showMemoryWord(text, targetEl) {
    /* Remove any active memory word first */
    if (activeMemory) {
      activeMemory.remove();
      activeMemory = null;
    }

    const span = document.createElement('span');
    span.className = 'scent-memory__word';
    span.textContent = text;
    span.setAttribute('aria-hidden', 'true');

    /* Position near the target element */
    const rect = targetEl.getBoundingClientRect();
    const offsetX = randomOffset(60, 160);
    const offsetY = randomOffset(40, 120);

    span.style.position = 'fixed';
    span.style.left = (rect.left + rect.width / 2 + offsetX) + 'px';
    span.style.top = (rect.top + rect.height / 2 + offsetY) + 'px';
    span.style.pointerEvents = 'none';
    span.style.zIndex = '50';
    span.style.fontFamily = 'var(--font-display)';
    span.style.fontStyle = 'italic';
    span.style.fontSize = 'var(--text-sm, 0.8125rem)';
    span.style.color = 'var(--color-mist, #8A8A8A)';
    span.style.whiteSpace = 'nowrap';
    span.style.userSelect = 'none';

    if (prefersReducedMotion.matches) {
      /* Static display for reduced motion users */
      span.style.opacity = '0.4';
      document.body.appendChild(span);
      activeMemory = span;

      setTimeout(function () {
        if (activeMemory === span) {
          span.remove();
          activeMemory = null;
        }
      }, 2600);
      return span;
    }

    /* Animated entrance */
    span.style.opacity = '0';
    span.style.transform = 'translateY(10px)';
    span.style.transition = 'opacity 600ms cubic-bezier(0.16, 1, 0.3, 1), transform 600ms cubic-bezier(0.16, 1, 0.3, 1)';

    document.body.appendChild(span);
    activeMemory = span;

    /* Trigger reflow then animate in */
    requestAnimationFrame(function () {
      span.style.opacity = '0.4';
      span.style.transform = 'translateY(0)';
    });

    /* Hold 2s, then fade out */
    setTimeout(function () {
      if (activeMemory !== span) return;
      span.style.transition = 'opacity 400ms ease, transform 400ms ease';
      span.style.opacity = '0';
      span.style.transform = 'translateY(-10px)';

      setTimeout(function () {
        if (activeMemory === span) {
          activeMemory = null;
        }
        span.remove();
      }, 420);
    }, 2000);

    return span;
  }

  /**
   * Initialize scent-hover interactions on [data-scent] elements.
   */
  function initScentHover() {
    const scentEls = document.querySelectorAll('[data-scent]');
    if (scentEls.length === 0) return;

    const eventName = isTouchDevice ? 'touchstart' : 'mouseenter';

    for (const el of scentEls) {
      el.addEventListener(eventName, function () {
        const key = (el.dataset.scent || '').toLowerCase().trim();
        const pool = SCENT_MEMORIES[key] || SCENT_MEMORIES['default'];
        const word = pick(pool);
        showMemoryWord(word, el);
      }, { passive: true });
    }
  }

  /**
   * Brand Story Scent Trail — MutationObserver
   * Watches for .brand-story__word elements gaining .is-visible class.
   * 15% chance a ghostly memory word appears near a newly revealed word.
   */
  function initBrandStoryTrail() {
    const storySection = document.querySelector('.brand-story');
    if (!storySection) return;
    if (prefersReducedMotion.matches) return;

    let trailCooldown = false;

    const observer = new MutationObserver(function (mutations) {
      if (trailCooldown) return;

      for (const mutation of mutations) {
        if (
          mutation.type === 'attributes' &&
          mutation.attributeName === 'class' &&
          mutation.target.classList.contains('brand-story__word') &&
          mutation.target.classList.contains('is-visible')
        ) {
          /* 15% chance to trigger */
          if (Math.random() > 0.15) continue;

          /* Throttle: one trail word at a time with cooldown */
          trailCooldown = true;
          const word = pick(STORY_MEMORIES);
          showMemoryWord(word, mutation.target);

          setTimeout(function () {
            trailCooldown = false;
          }, 3200);

          return;
        }
      }
    });

    observer.observe(storySection, {
      attributes: true,
      attributeFilter: ['class'],
      subtree: true
    });
  }

  /**
   * Boot everything on DOMContentLoaded.
   */
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () {
      initScentHover();
      initBrandStoryTrail();
    });
  } else {
    initScentHover();
    initBrandStoryTrail();
  }
})();
